import { useEffect, useState } from 'react';

/**
 * Aspect ratios (width / height) for a list of image URLs, measured by loading each one once.
 * The cover layouts need the shape of every photo before they can place it (§4: `side` and
 * `poster` widths follow the photo), and the DTO only carries dimensions for some uploads.
 *
 * Results are cached per URL for the session, so the page, the review previews and the wall
 * never measure the same picture twice. Returns `{ [src]: ratio }` for what has loaded so far.
 */
const cache = new Map();
const pending = new Map();

export function measureImageRatio(src) {
  if (!src) return Promise.resolve(null);
  if (cache.has(src)) return Promise.resolve(cache.get(src));
  if (pending.has(src)) return pending.get(src);
  const p = new Promise((resolve) => {
    if (typeof Image === 'undefined') { resolve(null); return; }
    const img = new Image();
    let done = false;
    const finish = (r) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      img.onload = null; img.onerror = null;
      if (r) cache.set(src, r);
      pending.delete(src);
      resolve(r);
    };
    const timer = setTimeout(() => finish(null), 8000);
    img.referrerPolicy = 'no-referrer';
    img.onload = () => finish(img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : null);
    img.onerror = () => finish(null);
    img.src = src;
  });
  pending.set(src, p);
  return p;
}

/** Record a ratio measured elsewhere (an <img onLoad>), so the hook answers on the next render. */
export function rememberImageRatio(src, ratio) {
  if (src && ratio > 0) cache.set(src, ratio);
}

export function knownImageRatio(src) {
  return cache.get(src) || null;
}

export default function useImageRatios(srcs = []) {
  const key = srcs.filter(Boolean).join('|');
  const [ratios, setRatios] = useState(() => {
    const out = {};
    srcs.forEach((s) => { if (s && cache.has(s)) out[s] = cache.get(s); });
    return out;
  });
  useEffect(() => {
    let cancelled = false;
    const list = key ? key.split('|') : [];
    // Anything already known lands synchronously in one update.
    const known = {};
    let any = false;
    list.forEach((s) => { if (cache.has(s)) { known[s] = cache.get(s); any = true; } });
    if (any) setRatios((prev) => ({ ...prev, ...known }));
    list.filter((s) => !cache.has(s)).forEach((s) => {
      measureImageRatio(s).then((r) => {
        if (!cancelled && r) setRatios((prev) => (prev[s] === r ? prev : { ...prev, [s]: r }));
      });
    });
    return () => { cancelled = true; };
  }, [key]);
  return ratios;
}
